import React, { useState } from 'react';
import { 
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ComposedChart, Area
} from 'recharts';
import './ReportBuilder.css';
import TimeRangeSelector from './TimeRangeSelector';
import DataTable from './DataTable';

const ReportBuilder = ({ dateRange, comparisonPeriod }) => {
    const [selectedMetrics, setSelectedMetrics] = useState(['support_rate', 'patch_rate']);
    const [visualization, setVisualization] = useState('line');
    const [filters, setFilters] = useState({
        districts: ['all'],
        demographics: ['all'],
        contactMethods: ['all']
    });

    // Generate advocacy campaign-specific mock data
    const generateMockData = () => {
        const data = [];
        let startDate;
        let days;
        
        // Use passed dateRange prop
        const range = dateRange?.preset || 'last30days';
        
        switch(range) {
            case 'today':
            case 'yesterday':
                days = 24; // Hourly data for single day
                break;
            case 'last7days':
                startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                days = 7;
                break;
            case 'last30days':
                startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                days = 30;
                break;
            case 'last90days':
                startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
                days = 90;
                break;
            case 'custom':
                if (dateRange?.start && dateRange?.end) {
                    startDate = new Date(dateRange.start);
                    days = Math.ceil((dateRange.end - dateRange.start) / (24 * 60 * 60 * 1000));
                } else {
                    startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                    days = 30;
                }
                break;
            default:
                startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                days = 30;
        }

        // Create base data patterns that trend upward for advocacy metrics
        const basePatches = 30;
        const baseSupport = 18;
        const baseCalls = 250;
 
        for (let i = 0; i < days; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            
            // Create upward trend with some variance
            const dayFactor = i / days; // Goes from 0 to almost 1
            const variance = Math.random() * 6 - 3; // -3 to +3 variance
            
            data.push({
                date: currentDate.toISOString().split('T')[0],
                support_rate: baseSupport + (dayFactor * 8) + variance,
                patch_rate: basePatches + (dayFactor * 15) + variance,
                call_volume: Math.round(baseCalls + (dayFactor * 320) + (Math.random() * 100 - 50)),
                ai_minutes: Math.round((baseCalls + (dayFactor * 320)) * 1.8 + (Math.random() * 60 - 30)),
                constituents_supporting: Math.round((baseCalls + (dayFactor * 320)) * (baseSupport/100) * (1 + dayFactor)),
                patches_completed: Math.round((baseCalls + (dayFactor * 320)) * (basePatches/100) * (1 + dayFactor))
            });
        }
        
        return data;
    };

    // Get current data based on date range
    const reportData = generateMockData();

    const availableMetrics = [
        { id: 'support_rate', label: 'Voter Agreement Rate (%)', color: '#9b51e0' },
        { id: 'patch_rate', label: 'Patch-Through Rate (%)', color: '#4A7FB9' },
        { id: 'call_volume', label: 'Total Calls', color: '#D22532' },
        { id: 'ai_minutes', label: 'AI Minutes Used', color: '#53B8BB' },
        { id: 'constituents_supporting', label: 'Constituents Supporting', color: '#6A67CE' },
        { id: 'patches_completed', label: 'Completed Patches', color: '#F67280' }
    ];

    const renderVisualization = () => {
        const COLORS = ['#9b51e0', '#4A7FB9', '#D22532', '#53B8BB', '#6A67CE', '#F67280'];
        
        switch(visualization) {
            case 'line':
                return (
                    <>
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={reportData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                {selectedMetrics.map((metric, idx) => (
                                    <Line 
                                        key={metric}
                                        type="monotone"
                                        dataKey={metric}
                                        name={availableMetrics.find(m => m.id === metric)?.label}
                                        stroke={availableMetrics.find(m => m.id === metric)?.color}
                                        strokeWidth={2}
                                        dot={{ r: 2 }}
                                        activeDot={{ r: 6 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                        
                        <div className="data-table-section">
                            <h3>Detailed Campaign Data</h3>
                            <DataTable 
                                data={reportData}
                                metrics={selectedMetrics}
                                metricLabels={availableMetrics}
                                filters={filters}
                            />
                        </div>
                    </>
                );
            case 'bar':
                return (
                    <>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={reportData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                {selectedMetrics.map((metric, idx) => (
                                    <Bar 
                                        key={metric}
                                        dataKey={metric}
                                        name={availableMetrics.find(m => m.id === metric)?.label}
                                        fill={availableMetrics.find(m => m.id === metric)?.color}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                        
                        <div className="data-table-section">
                            <h3>Detailed Campaign Data</h3>
                            <DataTable 
                                data={reportData}
                                metrics={selectedMetrics}
                                metricLabels={availableMetrics}
                                filters={filters}
                            />
                        </div>
                    </>
                );
            case 'pie':
                // For pie chart, we'll use aggregated data
                const aggregateData = selectedMetrics.map((metric, index) => {
                    const total = reportData.reduce((sum, day) => sum + (day[metric] || 0), 0);
                    return {
                        name: availableMetrics.find(m => m.id === metric)?.label,
                        value: total,
                        metric
                    };
                });
                
                return (
                    <>
                        <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                                <Pie
                                    data={aggregateData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={true}
                                    outerRadius={150}
                                    fill="#8884d8"
                                    dataKey="value"
                                    nameKey="name"
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                >
                                    {aggregateData.map((entry, index) => (
                                        <Cell 
                                            key={`cell-${index}`} 
                                            fill={availableMetrics.find(m => m.id === entry.metric)?.color} 
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                        
                        <div className="data-table-section">
                            <h3>Aggregated Campaign Data</h3>
                            <table className="campaign-table">
                                <thead>
                                    <tr>
                                        <th>Metric</th>
                                        <th>Total</th>
                                        <th>Daily Average</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedMetrics.map(metric => {
                                        const total = reportData.reduce((sum, day) => sum + (day[metric] || 0), 0);
                                        return (
                                            <tr key={metric}>
                                                <td>{availableMetrics.find(m => m.id === metric)?.label}</td>
                                                <td>{Math.round(total).toLocaleString()}</td>
                                                <td>{Math.round(total / reportData.length).toLocaleString()}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                );
            case 'composed':
                return (
                    <>
                        <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart data={reportData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip />
                                <Legend />
                                {selectedMetrics.map((metric, idx) => {
                                    if (metric === 'call_volume' || metric === 'ai_minutes' || 
                                        metric === 'constituents_supporting' || metric === 'patches_completed') {
                                        return (
                                            <Bar 
                                                key={metric}
                                                yAxisId="left"
                                                dataKey={metric}
                                                name={availableMetrics.find(m => m.id === metric)?.label}
                                                fill={availableMetrics.find(m => m.id === metric)?.color}
                                            />
                                        );
                                    } else {
                                        return (
                                            <Line 
                                                key={metric}
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey={metric}
                                                name={availableMetrics.find(m => m.id === metric)?.label}
                                                stroke={availableMetrics.find(m => m.id === metric)?.color}
                                                strokeWidth={2}
                                            />
                                        );
                                    }
                                })}
                            </ComposedChart>
                        </ResponsiveContainer>
                        
                        <div className="data-table-section">
                            <h3>Detailed Campaign Data</h3>
                            <DataTable 
                                data={reportData}
                                metrics={selectedMetrics}
                                metricLabels={availableMetrics}
                                filters={filters}
                            />
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="report-builder">
            <div className="report-builder-controls">
                <div className="control-section">
                    <h3>Campaign Metrics</h3>
                    <div className="metrics-grid">
                        {availableMetrics.map(metric => (
                            <div 
                                key={metric.id}
                                className={`metric-item ${selectedMetrics.includes(metric.id) ? 'selected' : ''}`}
                                onClick={() => {
                                    if (selectedMetrics.includes(metric.id)) {
                                        setSelectedMetrics(selectedMetrics.filter(m => m !== metric.id));
                                    } else {
                                        setSelectedMetrics([...selectedMetrics, metric.id]);
                                    }
                                }}
                            >
                                <div className="metric-color" style={{ backgroundColor: metric.color }} />
                                <span className="metric-label">{metric.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="control-section">
                    <h3>Visualization</h3>
                    <div className="visualization-options">
                        <button 
                            className={`viz-btn ${visualization === 'line' ? 'active' : ''}`}
                            onClick={() => setVisualization('line')}
                        >
                            Line Chart
                        </button>
                        <button 
                            className={`viz-btn ${visualization === 'bar' ? 'active' : ''}`}
                            onClick={() => setVisualization('bar')}
                        >
                            Bar Chart
                        </button>
                        <button 
                            className={`viz-btn ${visualization === 'pie' ? 'active' : ''}`}
                            onClick={() => setVisualization('pie')}
                        >
                            Pie Chart
                        </button>
                        <button 
                            className={`viz-btn ${visualization === 'composed' ? 'active' : ''}`}
                            onClick={() => setVisualization('composed')}
                        >
                            Combined Chart
                        </button>
                    </div>
                </div>

                <div className="control-section">
                    <h3>Filter Options</h3>
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Districts</label>
                            <select 
                                multiple 
                                value={filters.districts}
                                onChange={(e) => setFilters({
                                    ...filters,
                                    districts: Array.from(e.target.selectedOptions, option => option.value)
                                })}
                            >
                                <option value="all">All Districts</option>
                                <option value="district1">District 1</option>
                                <option value="district2">District 2</option>
                                <option value="district3">District 3</option>
                                <option value="district4">District 4</option>
                                <option value="district5">District 5</option>
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Demographics</label>
                            <select
                                multiple
                                value={filters.demographics}
                                onChange={(e) => setFilters({
                                    ...filters,
                                    demographics: Array.from(e.target.selectedOptions, option => option.value)
                                })}
                            >
                                <option value="all">All Demographics</option>
                                <option value="age18-29">Age: 18-29</option>
                                <option value="age30-45">Age: 30-45</option>
                                <option value="age46-65">Age: 46-65</option>
                                <option value="age65+">Age: 65+</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="report-visualization">
                {renderVisualization()}
            </div>

            <div className="report-actions">
                <button className="report-action-btn save-btn">Save Report</button>
                <button className="report-action-btn export-btn">Export Report</button>
                <button className="report-action-btn share-btn">Share Report</button>
                <button className="report-action-btn schedule-btn">Schedule Report</button>
            </div>
        </div>
    );
};

export default ReportBuilder; 